// Merkan · Anzeige während die KI einen Beleg liest (Rechnung, Angebot, Lieferschein, Kontoauszug).
// Einmal im Rahmen eingebunden; dokumentAuslesen() füllt den Lesestand. Zeigt echten Fortschritt (Schritte vom Server),
// erste Ergebnisse aus dem Strom (Absender, Nummer, Positionen) und wechselnde Sätze, damit sich die Wartezeit kürzer anfühlt.
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Check } from 'lucide-react';
import { lesen, teilErgebnis, type DokArt, type LeseStand } from '../lib/dokument';
import { useSession } from '../lib/session';
import { euro, datum } from '../lib/format';
import { t, tn } from '../lib/sprache';
import { IcAngebote, IcAnhang, IcERechnung, IcRechnungen, IcSchliessen, IcVerpackt, IcZahlungseingang } from './icons';
import './dokument-lesen.css';

const TITEL: Record<DokArt, () => string> = {
  rechnung: () => t('Deine Rechnung wird eingelesen'),
  angebot: () => t('Das Angebot wird eingelesen'),
  lieferschein: () => t('Dein Lieferschein wird eingelesen'),
  kontoauszug: () => t('Dein Kontoauszug wird eingelesen'),
  kundenauftrag: () => t('Der Auftrag wird eingelesen'),
  auto: () => t('Der Beleg wird eingelesen'),
};

// Sätze je Belegart während die KI liest; dazwischen etwas Seemannsgarn
const SAETZE_LESEN: Record<DokArt, () => string[]> = {
  rechnung: () => [t('Kopf und Absender werden gelesen'), t('Positionen werden Zeile für Zeile erfasst'), t('Teilenummern werden Zeichen für Zeichen übernommen'), t('Steuersätze und Summen werden gelesen'), t('Gleich siehst du das Ergebnis')],
  angebot: () => [t('Preise werden erfasst'), t('Lieferzeiten werden in Tage umgerechnet'), t('Teilenummern werden Zeichen für Zeichen übernommen'), t('Gleich siehst du das Angebot')],
  lieferschein: () => [t('Bestellnummern werden gesucht'), t('Gelieferte Mengen werden gezählt'), t('Handschriftliche Vermerke werden beachtet'), t('Gleich siehst du, zu welcher Bestellung er gehört')],
  kontoauszug: () => [t('Buchungen werden erfasst'), t('Soll und Haben werden sortiert'), t('Verwendungszwecke werden gelesen'), t('Gleich siehst du die Buchungen')],
  kundenauftrag: () => [t('Kunde und Bestellnummer werden gesucht'), t('Schiff und IMO werden gelesen'), t('Positionen werden Zeile für Zeile erfasst'), t('Gleich steht der Vorgang bereit')],
  auto: () => [t('Die Belegart wird bestimmt'), t('Inhalte werden erfasst'), t('Gleich siehst du das Ergebnis')],
};
// Augenzwinkern je Branche des Mandanten (Admin Center › Mandant › Branche); alles andere ist branchenunabhängig
const BRANCHENSAETZE: Record<string, () => string[]> = {
  maritim: () => [t('Der Lotse ist an Bord'), t('Kurs auf die Summenzeile'), t('Die Ladung wird gelöscht, Zeile für Zeile'), t('Fast im Hafen')],
  grosshandel: () => [t('Kartons werden ausgepackt, Zeile für Zeile'), t('Die Ware sucht ihren Lagerplatz'), t('Lieferschein trifft Lager'), t('Fast versandbereit')],
  handwerk: () => [t('Aufmaß wird genommen'), t('Zweimal messen, einmal buchen'), t('Die Wasserwaage liegt an'), t('Fast fertig montiert')],
  neutral: () => [t('Ordnung kommt in die Zahlen'), t('Jede Zeile bekommt ihren Platz'), t('Kurz durchatmen'), t('Gleich geschafft')],
};
const SAETZE_PRUEFEN = () => [t('Menge mal Preis, Zeile für Zeile nachgerechnet'), t('Lieferant wird im Stamm gesucht'), t('Artikel werden mit dem Stamm abgeglichen')];
const TIPPS = () => [
  t('E-Rechnungen als XML liest Merkan in unter einer Sekunde, ganz ohne KI.'),
  t('Mehrseitige Rechnungen: alle Fotos auf einmal auswählen, Merkan liest sie als ein Dokument.'),
  t('Fotos gerade und bei gutem Licht aufnehmen, dann liest {ki} am sichersten.'),
  t('Merkan rechnet jede Zeile im Code nach und meldet Abweichungen.'),
  t('Geänderte Bankverbindungen auf Rechnungen werden immer als Hinweis gemeldet.'),
  t('Deine Korrekturen in der Prüfmaske verbessern die Erkennung.'),
  t('Rechnungen aus Mails lassen sich im Service Hub direkt am Anhang auswerten.'),
];

// Erfahrungswerte aus PERFORMANCE.md (Sekunden für den KI-Teil): für einen ruhigen Fortschrittsbalken, nicht als Versprechen
const ERWARTET: Record<DokArt, number> = { rechnung: 10, angebot: 8, lieferschein: 7, kontoauszug: 10, kundenauftrag: 10, auto: 10 };

function Symbol({ art, xml }: { art: DokArt; xml: boolean }) {
  if (xml) return <IcERechnung size={22} />;
  if (art === 'rechnung') return <IcRechnungen size={22} />;
  if (art === 'angebot' || art === 'kundenauftrag') return <IcAngebote size={22} />;
  if (art === 'lieferschein') return <IcVerpackt size={22} />;
  if (art === 'kontoauszug') return <IcZahlungseingang size={22} />;
  return <IcAnhang size={22} />;
}

/** Sanfter Fortschritt: 0 bis 8 % beim Senden, bis 90 % asymptotisch beim Lesen, 95 % beim Prüfen, 100 % fertig */
function fortschritt(s: LeseStand, jetzt: number, zeilen: number): number {
  if (s.phase === 'fertig') return 1;
  if (s.phase === 'pruefen') return 0.95;
  if (s.phase === 'senden') return Math.min(0.08, ((jetzt - s.start) / 4000) * 0.08);
  const sek = (jetzt - (s.kiStart ?? jetzt)) / 1000;
  // Lange Belege: solange Zeilen eintreffen, wächst die erwartete Dauer mit, damit der Balken nicht früh stehen bleibt
  const tau = Math.max(ERWARTET[s.art] + (s.seiten - 1) * 6, zeilen * 0.6) * 0.8;
  return Math.min(0.9, 0.08 + 0.82 * (1 - Math.exp(-sek / tau)));
}

export function DokumentLesen() {
  const s = useSyncExternalStore(lesen.abonnieren, lesen.stand);
  const { mandant } = useSession();
  const [jetzt, setJetzt] = useState(Date.now());
  useEffect(() => {
    if (!s) return;
    const i = setInterval(() => setJetzt(Date.now()), 250);
    return () => clearInterval(i);
  }, [s?.id]);
  useEffect(() => {
    if (!s || s.hintergrund) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') lesen.inHintergrund(true); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [s?.id, s?.hintergrund]);

  const teil = useMemo(() => (s?.text ? teilErgebnis(s.text) : null), [s?.text]);
  if (!s) return null;

  const zeilen = (teil?.positionen.length ?? 0) + (teil?.buchungen.length ?? 0);
  const anteil = fortschritt(s, jetzt, zeilen);
  const sek = Math.max(0, Math.round((jetzt - s.start) / 1000));

  // Satz: echte Zwischenstände haben Vorrang, sonst wechselnde Sätze alle 3,2 s
  const takt = Math.floor((jetzt - s.start) / 3200);
  let satz: string;
  if (s.phase === 'fertig') satz = t('Fertig, gleich siehst du das Ergebnis');
  else if (s.phase === 'senden') satz = s.xml ? t('E-Rechnung wird direkt gelesen') : t('Datei wird übertragen');
  else if (s.phase === 'pruefen') satz = SAETZE_PRUEFEN()[takt % SAETZE_PRUEFEN().length];
  else if (teil?.aussteller && takt % 3 === 1) satz = s.art === 'kontoauszug' ? t('Auszug von {name} erkannt', { name: teil.aussteller }) : t('Beleg von {name} erkannt', { name: teil.aussteller });
  else if (zeilen && takt % 3 === 2) satz = s.art === 'kontoauszug' ? tn(zeilen, '{n} Buchung gelesen', '{n} Buchungen gelesen') : tn(zeilen, '{n} Position gelesen', '{n} Positionen gelesen');
  else {
    const liste = SAETZE_LESEN[s.art]();
    const garn = (BRANCHENSAETZE[mandant?.branche ?? 'neutral'] ?? BRANCHENSAETZE.neutral)();
    satz = takt % 4 === 3 ? garn[Math.floor(takt / 4) % garn.length] : liste[Math.min(takt - Math.floor(takt / 4), liste.length - 1)];
  }
  const tipp = TIPPS()[Math.floor((jetzt - s.start) / 8000 + s.id) % TIPPS().length];

  const schritte = [
    { name: t('Hochgeladen'), zustand: 'erledigt' },
    { name: s.xml ? t('E-Rechnung lesen') : t('{ki} liest den Beleg'), zustand: s.phase === 'senden' || s.phase === 'ki' ? 'aktuell' : 'erledigt' },
    { name: t('Nachrechnen'), zustand: s.phase === 'pruefen' ? 'aktuell' : s.phase === 'fertig' ? 'erledigt' : 'offen' },
    { name: t('Abgleich mit Lieferanten und Artikeln'), zustand: s.phase === 'fertig' ? 'erledigt' : s.schritte.some((x) => x.schritt === 'Lieferant im Stamm suchen') ? 'aktuell' : 'offen' },
  ];

  if (s.hintergrund) {
    return (
      <button type="button" className="dl-pille" onClick={() => lesen.inHintergrund(false)} aria-label={t('Lesestand anzeigen')}>
        <span className="dl-pille-symbol"><Symbol art={s.art} xml={s.xml} /></span>
        <span className="dl-pille-text">{s.phase === 'fertig' ? t('Fertig') : zeilen ? tn(zeilen, '{n} Zeile gelesen', '{n} Zeilen gelesen') : t('Wird gelesen')}</span>
        <span className="dl-balken dl-balken-klein" aria-hidden><span style={{ transform: `scaleX(${anteil})` }} /></span>
      </button>
    );
  }

  const pos = teil?.positionen ?? [];
  const buch = teil?.buchungen ?? [];
  return (
    <>
      <div className="schleier dl-schleier" onClick={() => lesen.inHintergrund(true)} />
      <div className="dl-karte" role="dialog" aria-modal="false" aria-labelledby="dl-titel">
        <div className="dl-kopf">
          <span className="dl-symbol" data-phase={s.phase}><Symbol art={s.art} xml={s.xml} /></span>
          <div className="dl-kopf-text">
            <h2 id="dl-titel">{s.xml ? t('Deine E-Rechnung wird eingelesen') : TITEL[s.art]()}</h2>
            <div className="dl-datei">{s.name} · {sek} s</div>
          </div>
          <button type="button" className="dl-zu" onClick={() => lesen.inHintergrund(true)} aria-label={t('Im Hintergrund weiterlesen')} title={t('Im Hintergrund weiterlesen')}><IcSchliessen size={18} /></button>
        </div>

        <div className="dl-balken" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(anteil * 100)} aria-label={t('Fortschritt')}>
          <span style={{ transform: `scaleX(${anteil})` }} />
        </div>

        <p className="dl-satz" key={satz} aria-hidden>{satz}</p>
        <p className="sr-only" aria-live="polite">{s.phase === 'ki' ? t('{ki} liest den Beleg') : s.phase === 'pruefen' ? t('Beträge werden nachgerechnet') : s.phase === 'fertig' ? t('Fertig') : ''}</p>

        <ol className="dl-schritte">
          {schritte.map((x) => (
            <li key={x.name} data-zustand={x.zustand}>
              <span className="dl-punkt">{x.zustand === 'erledigt' && <Check size={11} strokeWidth={3} />}</span>
              <span>{x.name}</span>
            </li>
          ))}
        </ol>

        {(teil?.aussteller || teil?.nummer || pos.length > 0 || buch.length > 0) && (
          <div className="dl-vorschau">
            <div className="dl-vorschau-kopf">
              <span className="dl-vorschau-titel">{t('Schon erkannt')}</span>
              {zeilen > 0 && <span className="dl-zaehler">{s.art === 'kontoauszug' ? tn(buch.length, '{n} Buchung', '{n} Buchungen') : tn(pos.length, '{n} Position', '{n} Positionen')}</span>}
            </div>
            <dl className="dl-felder">
              {teil?.aussteller && <><dt>{s.art === 'kontoauszug' ? t('Bank') : t('Absender')}</dt><dd>{teil.aussteller}</dd></>}
              {teil?.nummer && <><dt>{t('Nummer')}</dt><dd className="mono">{teil.nummer}</dd></>}
              {teil?.datum && <><dt>{t('Datum')}</dt><dd>{datum(teil.datum)}</dd></>}
              {teil?.brutto != null && teil.brutto !== 0 && <><dt>{t('Brutto')}</dt><dd className="betrag">{euro(teil.brutto)}</dd></>}
            </dl>
            {pos.length > 0 && (
              <ul className="dl-zeilen">
                {pos.slice(-5).map((p, i) => (
                  <li key={pos.length - Math.min(5, pos.length) + i}>
                    <span className="mono dl-nr">{p.artikelnummer || '·'}</span>
                    <span className="dl-bez">{p.bezeichnung}</span>
                    <span className="dl-menge">{p.menge ?? ''}{p.einheit ? ` ${p.einheit}` : ''}</span>
                    {s.art !== 'lieferschein' && <span className="betrag dl-betrag">{p.gesamt ? euro(p.gesamt) : ''}</span>}
                  </li>
                ))}
              </ul>
            )}
            {buch.length > 0 && (
              <ul className="dl-zeilen">
                {buch.slice(-5).map((b, i) => (
                  <li key={buch.length - Math.min(5, buch.length) + i}>
                    <span className="dl-nr">{datum(b.datum)}</span>
                    <span className="dl-bez">{b.name}</span>
                    <span className={`betrag dl-betrag${Number(b.betrag) < 0 ? ' dl-minus' : ''}`}>{euro(b.betrag)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <p className="dl-tipp"><strong>{t('Tipp')}:</strong> {tipp}</p>
        <div className="dl-fuss">
          <button type="button" className="dl-link" onClick={() => lesen.inHintergrund(true)}>{t('Im Hintergrund weiterlesen')}</button>
        </div>
      </div>
    </>
  );
}
